/**
 * Custom hook to sync pagination/sorting state with URL query params.
 * This preserves state when navigating back/forward in browser history.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UI_CONFIG } from '@aaas/shared';

interface UseUrlPaginationOptions {
  defaultSortBy?: string;
  defaultOrder?: 'asc' | 'desc';
  filterKeys?: string[]; // Optional filter keys to track in URL (single values)
  arrayFilterKeys?: string[]; // Filter keys that store arrays (multi-select)
}

interface UseUrlPaginationReturn {
  page: number;
  size: number;
  sortBy: string;
  order: 'asc' | 'desc';
  filters: Record<string, string | string[] | undefined>;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  setSort: (key: string, direction: 'asc' | 'desc') => void;
  setFilters: (filters: Record<string, string | string[] | undefined>) => void;
}

export function useUrlPagination(options: UseUrlPaginationOptions = {}): UseUrlPaginationReturn {
  const {
    defaultSortBy = 'created_at',
    defaultOrder = 'desc',
    filterKeys = [],
    arrayFilterKeys = [],
  } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Read state from URL
  const page = Number(searchParams.get('page')) || 1;
  const size = Number(searchParams.get('size')) || UI_CONFIG.PAGINATION.DEFAULT_PAGE_SIZE;
  const sortBy = searchParams.get('sort') || defaultSortBy;
  const order = (searchParams.get('order') as 'asc' | 'desc') || defaultOrder;

  // Read filters from URL (supports both single and array values)
  const filters = useMemo(() => {
    const result: Record<string, string | string[] | undefined> = {};

    // Single value filters
    filterKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        result[key] = value;
      }
    });

    // Array value filters (multi-select)
    arrayFilterKeys.forEach((key) => {
      const values = searchParams.getAll(key);
      if (values.length > 0) {
        result[key] = values;
      }
    });

    return result;
  }, [searchParams, filterKeys, arrayFilterKeys]);

  // Update functions
  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams(
        (prev) => {
          prev.set('page', String(newPage));
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSize = useCallback(
    (newSize: number) => {
      setSearchParams(
        (prev) => {
          prev.set('size', String(newSize));
          prev.set('page', '1'); // Reset to page 1 when changing page size
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSort = useCallback(
    (key: string, direction: 'asc' | 'desc') => {
      setSearchParams(
        (prev) => {
          prev.set('sort', key);
          prev.set('order', direction);
          prev.set('page', '1'); // Reset to page 1 when changing sort
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setFilters = useCallback(
    (newFilters: Record<string, string | string[] | undefined>) => {
      setSearchParams(
        (prev) => {
          // Update filter params (supports both single and array values)
          Object.entries(newFilters).forEach(([key, value]) => {
            prev.delete(key); // Clear existing values first
            if (Array.isArray(value)) {
              // Multi-select: append each value
              value.forEach((v) => prev.append(key, v));
            } else if (value) {
              prev.set(key, value);
            }
          });

          // Clear any single filter keys not in newFilters
          filterKeys.forEach((key) => {
            if (!(key in newFilters)) {
              prev.delete(key);
            }
          });

          // Clear any array filter keys not in newFilters
          arrayFilterKeys.forEach((key) => {
            if (!(key in newFilters)) {
              prev.delete(key);
            }
          });

          prev.set('page', '1'); // Reset to page 1 when changing filters
          return prev;
        },
        { replace: true }
      );
    },
    [setSearchParams, filterKeys, arrayFilterKeys]
  );

  return { page, size, sortBy, order, filters, setPage, setSize, setSort, setFilters };
}
